/**
 * Formata CNPJ (00.000.000/0000-00)
 */
export function formatCNPJ(cnpj: string | null | undefined): string {
  if (!cnpj) return '';

  // Remove caracteres não numéricos
  const cleaned = cnpj.replace(/\D/g, '');
  if (!cleaned) return '';

  if (cleaned.length <= 2) return cleaned;
  if (cleaned.length <= 5) return `${cleaned.slice(0, 2)}.${cleaned.slice(2)}`;
  if (cleaned.length <= 8) return `${cleaned.slice(0, 2)}.${cleaned.slice(2, 5)}.${cleaned.slice(5)}`;
  if (cleaned.length <= 12) return `${cleaned.slice(0, 2)}.${cleaned.slice(2, 5)}.${cleaned.slice(5, 8)}/${cleaned.slice(8)}`;
  return `${cleaned.slice(0, 2)}.${cleaned.slice(2, 5)}.${cleaned.slice(5, 8)}/${cleaned.slice(8, 12)}-${cleaned.slice(12, 14)}`;
}

/**
 * Formata CPF (000.000.000-00)
 */
export function formatCPF(cpf: string | null | undefined): string {
  if (!cpf) return '';

  // Remove caracteres não numéricos
  const cleaned = cpf.replace(/\D/g, '');
  if (!cleaned) return '';

  if (cleaned.length <= 3) return cleaned;
  if (cleaned.length <= 6) return `${cleaned.slice(0, 3)}.${cleaned.slice(3)}`;
  if (cleaned.length <= 9) return `${cleaned.slice(0, 3)}.${cleaned.slice(3, 6)}.${cleaned.slice(6)}`;
  return `${cleaned.slice(0, 3)}.${cleaned.slice(3, 6)}.${cleaned.slice(6, 9)}-${cleaned.slice(9, 11)}`;
}

/**
 * Formata telefone (00) 00000-0000 ou (00) 0000-0000
 * Alias para formatTelefone para compatibilidade
 */
export function formatPhone(phone: string | null | undefined): string {
  if (!phone) return '';

  // Remove caracteres não numéricos
  const cleaned = phone.replace(/\D/g, '');
  if (!cleaned) return '';

  // Telefone celular (11 dígitos)
  if (cleaned.length === 11) {
    return cleaned.replace(
      /^(\d{2})(\d{5})(\d{4})$/,
      '($1) $2-$3'
    );
  }

  // Telefone fixo (10 dígitos)
  if (cleaned.length === 10) {
    return cleaned.replace(
      /^(\d{2})(\d{4})(\d{4})$/,
      '($1) $2-$3'
    );
  }

  // Se tiver menos de 10 dígitos, formata progressivo
  if (cleaned.length <= 2) return `(${cleaned}`;
  if (cleaned.length <= 6) return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2)}`;
  return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 6)}-${cleaned.slice(6)}`;
}

/**
 * Formata CEP (00000-000)
 */
export function formatCEP(cep: string | null | undefined): string {
  if (!cep) return '';

  const cleaned = cep.replace(/\D/g, '');
  if (!cleaned) return '';

  if (cleaned.length <= 5) return cleaned;
  return `${cleaned.slice(0, 5)}-${cleaned.slice(5, 8)}`;
}

/**
 * Limpa formatação do CEP e retorna apenas números
 */
export function cleanCEP(value: string): string {
  return value.replace(/\D/g, '');
}

/**
 * Formata valor monetário em BRL (R$)
 */
export function formatCurrency(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === '') {
    return 'R$ 0,00';
  }

  const numValue = typeof value === 'string' ? parseFloat(value) : value;

  if (isNaN(numValue)) {
    return 'R$ 0,00';
  }

  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(numValue);
}

/**
 * Formata número com separador de milhares
 */
export function formatNumber(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === '') {
    return '0';
  }

  const numValue = typeof value === 'string' ? parseFloat(value) : value;

  if (isNaN(numValue)) {
    return '0';
  }

  return new Intl.NumberFormat('pt-BR').format(numValue);
}

/**
 * Formata telefone (00) 00000-0000 ou (00) 0000-0000
 * Alias para formatPhone para compatibilidade com scheduling
 */
export function formatTelefone(phone: string | null | undefined): string {
  return formatPhone(phone);
}

/**
 * Remove formatação de CNPJ, CPF ou telefone
 */
export function unformat(value: string | null | undefined): string {
  if (!value) return '';
  return value.replace(/\D/g, '');
}

/**
 * Detecta e formata automaticamente CNPJ, CPF, CEP, telefone ou valores monetários baseado no nome do campo
 */
export function autoFormatField(key: string, value: unknown): string {
  if (value === null || value === undefined || value === '') {
    return 'N/A';
  }

  const stringValue = String(value);
  const lowerKey = key.toLowerCase();

  // Detectar valores monetários por nome do campo (prioridade alta)
  if (lowerKey.includes('preco') || lowerKey.includes('valor') || lowerKey.includes('custo') || lowerKey.includes('price') || lowerKey.includes('price_per')) {
    const numValue = typeof value === 'number' ? value : parseFloat(stringValue);
    if (!isNaN(numValue)) {
      return formatCurrency(numValue);
    }
  }

  // Detectar por nome do campo (prioridade alta)
  if (lowerKey.includes('cnpj')) {
    return formatCNPJ(stringValue);
  }

  if (lowerKey.includes('cpf')) {
    return formatCPF(stringValue);
  }

  if (lowerKey.includes('cep')) {
    return formatCEP(stringValue);
  }

  if (lowerKey.includes('telefone') || lowerKey.includes('phone') || lowerKey.includes('celular') || lowerKey.includes('fone')) {
    return formatPhone(stringValue);
  }

  // Se não detectar pelo nome, tenta detectar pelo formato do valor
  const cleaned = stringValue.replace(/\D/g, '');

  // Se não tiver dígitos, retorna original
  if (cleaned.length === 0) {
    return stringValue;
  }

  // CNPJ: 14 dígitos (formato específico)
  if (cleaned.length === 14) {
    // Verifica se não é um número muito grande (provavelmente não é CNPJ se for número puro muito grande)
    const numValue = parseFloat(cleaned);
    if (numValue < 10000000000000) { // Limite razoável para CNPJ
      return formatCNPJ(stringValue);
    }
  }

  // CPF: 11 dígitos (formato específico)
  if (cleaned.length === 11) {
    const numValue = parseFloat(cleaned);
    // CPF válido tem 11 dígitos e não é um número muito grande
    if (numValue < 100000000000) {
      return formatCPF(stringValue);
    }
  }

  // CEP: 8 dígitos (formato específico)
  if (cleaned.length === 8) {
    const numValue = parseFloat(cleaned);
    // CEP válido tem 8 dígitos
    if (numValue < 100000000) {
      return formatCEP(stringValue);
    }
  }

  // Telefone: 10 ou 11 dígitos (formato específico)
  if (cleaned.length === 10 || cleaned.length === 11) {
    const numValue = parseFloat(cleaned);
    // Telefone válido tem 10 ou 11 dígitos e não é um número muito grande
    if (cleaned.length === 10 && numValue < 10000000000) {
      return formatPhone(stringValue);
    }
    if (cleaned.length === 11 && numValue < 100000000000) {
      return formatPhone(stringValue);
    }
  }

  // Se não for nenhum dos formatos conhecidos, retorna o valor original
  return stringValue;
}

/**
 * Mascara e-mail parcialmente para exibição amigável e segura (ex: jo***a@empresa.com.br)
 */
export function maskEmail(email: string | null | undefined): string {
  if (!email || typeof email !== 'string') return '';
  const trimmed = email.trim();
  if (!trimmed.includes('@')) return trimmed;

  const [user, domain] = trimmed.split('@');
  if (!user || !domain) return trimmed;

  if (user.length <= 2) {
    return `${user[0]}***@${domain}`;
  }
  if (user.length <= 4) {
    return `${user.slice(0, 2)}***@${domain}`;
  }
  return `${user.slice(0, 2)}***${user.slice(-1)}@${domain}`;
}

/**
 * Mascara telefone parcialmente (ex: (11) 9****-4321 ou (11) ****-4444)
 */
export function maskPhone(phone: string | null | undefined): string {
  if (!phone || typeof phone !== 'string') return '';
  const cleaned = phone.replace(/\D/g, '');

  // Celular com 11 dígitos: (DD) 9XXXX-XXXX -> (DD) 9****-XXXX
  if (cleaned.length === 11) {
    return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 3)}****-${cleaned.slice(7)}`;
  }

  // Telefone fixo com 10 dígitos: (DD) XXXX-XXXX -> (DD) ****-XXXX
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 2)}) ****-${cleaned.slice(6)}`;
  }

  if (cleaned.length > 4) {
    return `${cleaned.slice(0, 2)}****${cleaned.slice(-2)}`;
  }

  return phone;
}

/**
 * Alias de maskPhone para compatibilidade
 */
export function maskTelefone(phone: string | null | undefined): string {
  return maskPhone(phone);
}

/**
 * Formata o nome de exibição da empresa para o Portal do Cliente,
 * adicionando a identificação da filial (ex: "CORELLO (0030/00)" ou "CORELLO (Matriz)")
 * para permitir que clientes com múltiplas unidades com a mesma razão social
 * diferenciem facilmente qual filial estão visualizando.
 */
export function formatEmpresaDisplayName(
  emp?: { nome?: string; razao_social?: string; cnpj?: string } | null,
): string {
  if (!emp) return '';
  const nomeBase = emp.nome || emp.razao_social || 'Empresa';
  if (!emp.cnpj) return nomeBase;

  const digits = String(emp.cnpj).replace(/\D/g, '');
  if (digits.length === 14) {
    const filial = digits.slice(8, 12);
    const dv = digits.slice(12, 14);
    const sufixo = filial === '0001' ? 'Matriz' : `${filial}/${dv}`;
    return `${nomeBase} (${sufixo})`;
  }
  return nomeBase;
}
